// balanceModules.js
// -----------------------------------------------------------------------------
// Single source of truth for every module that can create a balance a family
// owes. The list view (FamilyBalances) and the statement (FamilyStatementModal)
// both read from here, so totals can never drift apart.
//
// COLONIE IS INTENTIONALLY EXCLUDED for now (not listed below).
//
// Field mappings below are derived from the actual backend:
//   - afterschool: vw_AfterschoolEnrollmentsWithBalance (TotalCost, SiblingDiscount,
//                  FinalCost, AmountPaid, TrueBalance/BalanceRemaining)
//   - bus:         BusEnrollments (TrimesterPrice, DiscountAmount, AmountPaid,
//                  BalanceRemaining) — ONE ROW PER TRIMESTER
//   - activities:  vw_ActivityEnrollmentsDetails (TotalCost, DiscountAmount,
//                  FinalCost, AmountPaid, BalanceRemaining).
//                  NOTE: TotalCharges is a COUNT, not money — do not use it.
//   - artschool:   vw_ArtSchoolEnrollments (BasePrice, FinalPrice, AmountPaid,
//                  BalanceRemaining). FAMILY PARAM IS `familyID` (capital ID).
//   - daycare:     DaycareEnrollments (monthly plan). ⚠️ true outstanding is
//                  charge-based (MonthlyCharges) and may need /daycare/charges.
//
// Each module exposes normalize(rawRow) -> line item:
//   { studentName, grade, description, billing, note?, cost, discount, final, paid, balance }
// -----------------------------------------------------------------------------

const fullName = (e) => (e.StudentName || `${e.FirstName || ''} ${e.LastName || ''}`).trim();
const gradeOf = (e) => e.Grade || e.Class || '-';
const num = (v) => Number(v) || 0;

// Charge statuses that aren't real money owed/paid — excluded from the ledger.
const DAYCARE_DEAD_STATUSES = ['Cancelled', 'Canceled', 'Void', 'Voided', 'Waived', 'Deleted'];

// "October 2025" from a charge's month/year.
const daycareMonth = (month, year) => {
  const d = new Date(Number(year), Number(month) - 1, 1);
  return `${d.toLocaleString('en-US', { month: 'long' })} ${year}`;
};

export const BALANCE_MODULES = [
  {
    key: 'afterschool',
    label: 'CAP',
    endpoint: '/afterschool/enrollments',
    descriptionLabel: 'Package',
    familyParam: 'familyId',
    yearParam: 'academicYear',
    // Afterschool runs on the clean ledger: every figure here is CUMULATIVE and
    // comes from MonthlyCharges via the view. Do NOT reach for the legacy
    // enrollment columns — TotalCost/SiblingDiscount/FinalCost are the CURRENT
    // RATE (per month, or per day for daily packages), and TotalMonthlyCharges/
    // AmountPaid/BalanceRemaining were frozen at migration.
    //
    // The previous mapping did `FinalCost + TotalMonthlyCharges`, i.e. one extra
    // month's rate on top of the billed total (family 7369-46541 read $2799
    // instead of $2488), and `AmountPaid`, which misattributes payments between
    // siblings. Both looked plausible because the family-level balance was right.
    normalize: (e) => ({
      studentName: fullName(e),
      grade: gradeOf(e),
      description: e.PackageName || '-',
      billing: e.BillingType || 'Monthly',
      note:
        num(e.ChargeCount) > 0
          ? `${num(e.ChargeCount)} charge${num(e.ChargeCount) !== 1 ? 's' : ''}`
          : null,
      cost: num(e.GrossToDate),
      discount: num(e.DiscountToDate),
      final: num(e.ChargedToDate),
      paid: num(e.PaidToDate),
      balance: num(e.TrueBalance),
    }),
  },
  {
    key: 'bus',
    label: 'Bus Transportation',
    endpoint: '/bus/enrollments',
    descriptionLabel: 'Route',
    familyParam: 'familyId',
    yearParam: 'academicYear',
    normalize: (e) => ({
      studentName: fullName(e),
      grade: e.Class || e.Grade || '-',
      description:
        e.LineName ||
        e.ServiceDescription ||
        (e.LineNumber != null ? `Line ${e.LineNumber}` : 'Bus'),
      // Bus is billed per trimester — show which one so rows aren't ambiguous.
      billing: e.Trimester != null ? `Trimester ${e.Trimester}` : 'Trimester',
      cost: num(e.TrimesterPrice),
      discount: num(e.DiscountAmount),
      final: num(e.TrimesterPrice) - num(e.DiscountAmount),
      paid: num(e.AmountPaid),
      balance: e.BalanceRemaining ?? 0,
    }),
  },
  {
    key: 'activities',
    label: 'Activities',
    endpoint: '/activities/enrollments',
    descriptionLabel: 'Activity',
    familyParam: 'familyId',
    yearParam: 'academicYear',
    normalize: (e) => ({
      studentName: fullName(e),
      grade: e.Grade || e.Class || '-',
      description: e.ActivityName || e.CategoryName || '-',
      billing: e.ActivityType === 'AfterSchoolSport' ? 'Sport' : 'Activity',
      // Use real money columns — NOT TotalCharges (that's a count -> the $1 bug).
      cost: e.TotalCost != null ? num(e.TotalCost) : num(e.FinalCost),
      discount: num(e.DiscountAmount),
      final: e.FinalCost != null ? num(e.FinalCost) : num(e.TotalCost),
      paid: num(e.AmountPaid),
      balance: e.BalanceRemaining ?? 0,
    }),
  },
  {
    key: 'daycare',
    label: 'Daycare',
    descriptionLabel: 'Charge',
    // Daycare is charge-based, not enrollment-based: the real outstanding lives
    // in MonthlyCharges (monthly charges + extras + negative-ReferenceID "old
    // balances"). So daycare supplies its own loaders instead of the default
    // enrollments-array path.

    // STATEMENT (one family): the FULL charge ledger — every monthly charge,
    // extra, and negative-ReferenceID old balance, paid or unpaid. We compute
    // each line's balance as FinalAmount - AmountPaid. Using the full ledger
    // (not just outstanding charges) is what makes "Total Paid" reflect reality
    // instead of collapsing to $0.
    loadStatementRows: async (apiCall, { familyId }) => {
      if (!familyId) return [];
      try {
        // Daycare runs an all-years rolling ledger — a family's outstanding
        // daycare balance carries across academic years — so the statement lists
        // every daycare charge regardless of the year being viewed. (academicYear
        // = 'all' tells the endpoint to skip its year filter.)
        const raw = await apiCall(
          `/daycare/charges?familyId=${encodeURIComponent(familyId)}&academicYear=all`
        );
        const charges = (Array.isArray(raw) ? raw : []).filter(
          (c) => !DAYCARE_DEAD_STATUSES.includes(String(c.Status || ''))
        );
        return charges
          .slice()
          // Old balances first, then chronological.
          .sort((a, b) => {
            const ao = a.ReferenceID < 0 ? 0 : 1;
            const bo = b.ReferenceID < 0 ? 0 : 1;
            if (ao !== bo) return ao - bo;
            return (num(a.ChargeYear) * 100 + num(a.ChargeMonth)) - (num(b.ChargeYear) * 100 + num(b.ChargeMonth));
          })
          .map((c) => {
            const final = num(c.FinalAmount);
            const paid = num(c.AmountPaid);
            const isOld = c.ReferenceID < 0 || c.ChargeType === 'Old Balance';
            const name =
              c.FirstName || c.LastName
                ? `${c.FirstName || ''} ${c.LastName || ''}`.trim()
                : isOld
                ? 'Old Balance'
                : 'Daycare';
            return {
              studentName: name,
              grade: '-',
              description: isOld
                ? c.Description || 'Old Balance'
                : c.ChargeMonth && c.ChargeYear
                ? daycareMonth(c.ChargeMonth, c.ChargeYear)
                : c.Description || c.ChargeType || 'Daycare Charge',
              billing: c.ChargeType || 'Monthly Charge',
              cost: num(c.Amount != null ? c.Amount : c.FinalAmount),
              discount: num(c.DiscountAmount),
              final,
              paid,
              balance: final - paid,
            };
          });
      } catch (err) {
        return [];
      }
    },

    // LIST (all families): per-family total (current + old balance).
    loadFamilyTotals: async (apiCall, { academicYear }) => {
      if (!academicYear) return [];
      try {
        const rows = await apiCall(
          `/daycare/balance-report?academicYear=${encodeURIComponent(academicYear)}`
        );
        return (Array.isArray(rows) ? rows : []).map((r) => ({
          FamilyID: r.FamilyID,
          FatherName: r.FamilyName || '',
          MotherName: '',
          PrimaryPhone: '',
          balance: num(r.TotalBalance),
        }));
      } catch (err) {
        return [];
      }
    },
  },
  {
    key: 'artschool',
    label: 'Art School',
    endpoint: '/artschool/enrollments',
    descriptionLabel: 'Class',
    familyParam: 'familyID', // ⚠️ capital ID — backend ignores `familyId`
    yearParam: 'academicYear',
    normalize: (e) => {
      const base = num(e.BasePrice);
      const final = e.FinalPrice != null ? num(e.FinalPrice) : base;
      return {
        studentName: fullName(e),
        grade: e.Class || e.Grade || '-',
        description: e.ClassName || e.CategoryName || 'Art School',
        billing: e.BillingType || 'Monthly',
        cost: base,
        discount: base > final ? base - final : num(e.DiscountAmount),
        final,
        paid: num(e.AmountPaid),
        balance: e.BalanceRemaining ?? 0,
      };
    },
  },
  {
    key: 'sis',
    label: 'Tuition (SIS)',
    descriptionLabel: 'Charge',
    // Tuition lives in the school's separate SIS database, not here, so this
    // module supplies its own loaders instead of the enrollments-array path.
    //
    // DUAL CURRENCY: SIS bills in both LBP and USD. A row carries its USD
    // figures in the normal cost/discount/final/paid/balance fields and its
    // Lebanese-pound figures in the *LBP mirror fields, which the statement
    // totals separately. A given row is one currency only. Never add the two
    // together.
    //
    // SIS is reachable on the school network only. fetchSisBalance answers
    // null off-network, and every loader degrades to empty — the rest of the
    // statement is unaffected.
    loadStatementRows: async (apiCall, { familyId, academicYear }) => {
      const data = await fetchSisBalance(apiCall, familyId, academicYear);
      if (!data || data.reachable === false) return [];

      const toRow = (r, label, isPrevious) => {
        const lbp = r.currency === 'LBP';
        const cost = num(r.charged);
        const discount = num(r.discount);
        const final = r.final != null ? num(r.final) : cost - discount;
        return {
          studentName: isPrevious ? 'Previous years' : 'Tuition',
          grade: '-',
          description: label,
          billing: r.currency,
          currency: r.currency,
          // USD side (0 on LBP rows)
          cost: lbp ? 0 : cost,
          discount: lbp ? 0 : discount,
          final: lbp ? 0 : final,
          paid: lbp ? 0 : num(r.paid),
          balance: lbp ? 0 : num(r.balance),
          // LBP mirror (0 on USD rows) — surfaced by the statement's LBP totals
          // and by the detailed card's per-currency rendering.
          finalLBP: lbp ? final : 0,
          discountLBP: lbp ? discount : 0,
          paidLBP: lbp ? num(r.paid) : 0,
          balanceLBP: lbp ? num(r.balance) : 0,
        };
      };

      const current = (data.currentYear || []).map((r) =>
        toRow(r, r.description ? `${r.chargeType} — ${r.description}` : r.chargeType, false)
      );
      // Backend already filters these to balance > 0 per currency.
      const previous = (data.previousYears || []).map((r) =>
        toRow(r, 'Carried over from previous years', true)
      );

      return [...previous, ...current];
    },
    // Individual SIS payments, shaped like our own so they slot straight into
    // the statement's "Payments Received" table.
    loadPayments: async (apiCall, { familyId, academicYear }) => {
      const data = await fetchSisBalance(apiCall, familyId, academicYear);
      if (!data || data.reachable === false) return [];
      return data.payments || [];
    },
  },
];

// -----------------------------------------------------------------------------
// SIS is slow (15s timeout) and school-network only. The statement loads its
// rows and its payments from the same endpoint, so memoise the in-flight
// request per family+year — both callers share one network round-trip, and a
// failure resolves to null so callers just render nothing.
// -----------------------------------------------------------------------------
let _sisMemo = { key: null, promise: null };
function fetchSisBalance(apiCall, familyId, academicYear) {
  if (!familyId || !academicYear) return Promise.resolve(null);
  const key = `${familyId}|${academicYear}`;
  if (_sisMemo.key !== key) {
    _sisMemo = {
      key,
      promise: apiCall(
        `/sis/family-balance?familyId=${encodeURIComponent(familyId)}&academicYear=${encodeURIComponent(academicYear)}`
      ).catch(() => null),
    };
  }
  return _sisMemo.promise;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

// Build the query string for a module using ITS family/year param names.
export function buildModuleParams(module, { familyId, academicYear } = {}) {
  const p = new URLSearchParams();
  if (familyId != null && familyId !== '') p.set(module.familyParam || 'familyId', familyId);
  if (academicYear != null && academicYear !== '') p.set(module.yearParam || 'academicYear', academicYear);
  return p.toString();
}

// Fetch one module's enrollments. A missing/unbuilt endpoint is treated as
// "no rows" so it never breaks the page. `opts` = { familyId?, academicYear? }.
export async function fetchModuleEnrollments(apiCall, module, opts) {
  try {
    const qs = buildModuleParams(module, opts);
    const raw = await apiCall(`${module.endpoint}${qs ? `?${qs}` : ''}`);
    return Array.isArray(raw) ? raw : [];
  } catch (err) {
    return [];
  }
}

export const emptyTotals = () => ({ cost: 0, discount: 0, final: 0, paid: 0, balance: 0 });

export const sumRows = (rows) =>
  rows.reduce(
    (t, r) => ({
      cost: t.cost + (r.cost || 0),
      discount: t.discount + (r.discount || 0),
      final: t.final + (r.final || 0),
      paid: t.paid + (r.paid || 0),
      balance: t.balance + (r.balance || 0),
    }),
    emptyTotals()
  );

// Currency-aware formatter. Charges are USD; payments may be USD or LBP.
// Lebanese pounds show with thousands separators + an "LBP" suffix. SIS calls
// this currency 'LL'; we display it as LBP everywhere for consistency.
export const formatMoney = (n, currency) => {
  const v = Number(n) || 0;
  if (currency && /lbp|ll|lira|ل\.ل/i.test(String(currency))) {
    return `${Math.round(v).toLocaleString('en-US')} LBP`;
  }
  return `$${v.toFixed(2)}`;
};

export const isLBP = (currency) => !!currency && /lbp|ll|lira|ل\.ل/i.test(String(currency));
